import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, FormField, Input } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

import type { AiSettings, ProviderInfo } from './types.js';

const PROVIDERS: Array<{ key: 'anthropic' | 'openai' | 'blackbox' | 'gemini'; label: string; keyField: keyof AiSettingsPatch }> = [
  { key: 'anthropic', label: 'Anthropic Claude', keyField: 'anthropicApiKey' },
  { key: 'openai', label: 'OpenAI (ChatGPT)', keyField: 'openaiApiKey' },
  { key: 'blackbox', label: 'BlackBox AI', keyField: 'blackboxApiKey' },
  { key: 'gemini', label: 'Google Gemini', keyField: 'geminiApiKey' },
];

interface AiSettingsPatch {
  defaultProvider?: string;
  defaultModel?: string;
  systemPrompt?: string | null;
  anthropicApiKey?: string;
  openaiApiKey?: string;
  blackboxApiKey?: string;
  geminiApiKey?: string;
  allowWriteTools?: boolean;
}

export function AISettingsPage(): JSX.Element {
  const { t } = useTranslation('ai');
  const [settings, setSettings] = useState<AiSettings | null>(null);
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [patch, setPatch] = useState<AiSettingsPatch>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void Promise.all([
      api().get<AiSettings>('/ai/settings'),
      api().get<{ items: ProviderInfo[] }>('/ai/providers'),
    ])
      .then(([s, p]) => {
        setSettings(s.data);
        setProviders(p.data.items);
      })
      .catch((e) => setError((e as Error).message));
  }, []);

  if (!settings) {
    return (
      <Card>
        <p className="text-sm text-fg-muted">{t('settings.title')}…</p>
      </Card>
    );
  }

  const onSave = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, unknown> = { ...patch };
      // Only send the system prompt if the user changed it.
      const r = await api().put<AiSettings>('/ai/settings', body);
      setSettings(r.data);
      setPatch({});
      setSavedAt(Date.now());
      // Re-fetch providers to refresh configured flags.
      const p = await api().get<{ items: ProviderInfo[] }>('/ai/providers');
      setProviders(p.data.items);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('settings.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('settings.description')}</p>
      </header>

      <Card>
        <h2 className="mb-3 text-base font-semibold">{t('settings.keys')}</h2>
        <p className="mb-4 text-xs text-fg-muted">{t('settings.keysHelp')}</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {PROVIDERS.map((p) => {
            const provInfo = providers.find((pi) => pi.name === p.key);
            const isSetNow = settings[`has${capitalize(p.key)}Key` as keyof AiSettings] as boolean;
            const fieldId = `ai-key-${p.key}`;
            const value = (patch[p.keyField] as string | undefined) ?? '';
            return (
              <FormField
                key={p.key}
                id={fieldId}
                label={
                  <span className="flex items-center gap-2">
                    {p.label}
                    {provInfo?.configured ? (
                      <Badge tone="success">
                        {provInfo.source === 'tenant' ? t('settings.keySet') : t('settings.keySet')}
                      </Badge>
                    ) : (
                      <Badge tone="neutral">{t('settings.keyMissing')}</Badge>
                    )}
                  </span> as unknown as string
                }
              >
                <Input
                  id={fieldId}
                  type="password"
                  autoComplete="off"
                  placeholder={isSetNow ? '••••••••••' : 'paste API key here'}
                  value={value}
                  onChange={(e) => setPatch((c) => ({ ...c, [p.keyField]: e.target.value }))}
                />
              </FormField>
            );
          })}
        </div>
      </Card>

      <Card>
        <h2 className="mb-3 text-base font-semibold">{t('settings.title')}</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormField id="ai-default-provider" label={t('settings.defaultProvider')}>
            <select
              id="ai-default-provider"
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              value={patch.defaultProvider ?? settings.defaultProvider}
              onChange={(e) => setPatch((c) => ({ ...c, defaultProvider: e.target.value }))}
            >
              {PROVIDERS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </FormField>
          <FormField id="ai-default-model" label={t('settings.defaultModel')}>
            <Input
              id="ai-default-model"
              value={patch.defaultModel ?? settings.defaultModel}
              onChange={(e) => setPatch((c) => ({ ...c, defaultModel: e.target.value }))}
            />
          </FormField>
          <FormField id="ai-system-prompt" label={t('settings.systemPrompt')}>
            <textarea
              id="ai-system-prompt"
              rows={5}
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg"
              placeholder={t('settings.systemPromptPlaceholder')}
              value={patch.systemPrompt !== undefined ? patch.systemPrompt ?? '' : settings.systemPrompt ?? ''}
              onChange={(e) => setPatch((c) => ({ ...c, systemPrompt: e.target.value }))}
            />
          </FormField>
          <FormField id="ai-allow-writes" label={t('settings.allowWriteTools')}>
            <label className="flex items-center gap-2 text-sm">
              <input
                id="ai-allow-writes"
                type="checkbox"
                className="h-4 w-4"
                checked={patch.allowWriteTools ?? settings.allowWriteTools}
                onChange={(e) => setPatch((c) => ({ ...c, allowWriteTools: e.target.checked }))}
              />
              <span className="text-fg-muted">
                {(patch.allowWriteTools ?? settings.allowWriteTools) ? 'on' : 'off'}
              </span>
            </label>
          </FormField>
        </div>
      </Card>

      {error && (
        <div className="rounded-md border border-danger bg-danger/5 px-3 py-2 text-sm text-danger" role="alert">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button onClick={onSave} loading={saving} disabled={Object.keys(patch).length === 0}>
          {t('settings.save')}
        </Button>
        {savedAt && Date.now() - savedAt < 4_000 && (
          <span className="text-sm text-success">{t('settings.saved')}</span>
        )}
      </div>
    </div>
  );
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0]!.toUpperCase() + s.slice(1);
}
